import { useState } from "react";
import { toast } from "sonner";
import { useSite } from "@/lib/content";
import { checkField, clean, rules } from "@/lib/adminApi";
import { Checkbox, FieldEditor, LiveIndicator, SaveButton, SectionHeader, useSaver } from "@/pages/admin/ui";

const FIELDS = [
  ["phone", "Telefon", "+90 216 123 45 67", rules.phone],
  ["whatsapp", "WhatsApp", "+90 532 123 45 67", rules.whatsapp],
  ["instagram", "Instagram", "@woodiapolonez", rules.instagram],
];
const num = (s) => Number(String(s).trim().replace(",", "."));

function coordError(lat, lng) {
  if (!lat.trim() && !lng.trim()) return null;
  if (!lat.trim() || !lng.trim()) return "Enlem ve boylamın ikisini de girin.";
  const a = num(lat), b = num(lng);
  if (Number.isNaN(a) || Number.isNaN(b)) return "Enlem ve boylam sayı olmalıdır (örn. 41.1061554).";
  if (a < -90 || a > 90 || b < -180 || b > 180) return "Enlem -90/90, boylam -180/180 aralığında olmalıdır.";
  return null;
}

export default function ContactSection() {
  const { content } = useSite();
  const [contact, setContact] = useState(() => ({ ...content.contact }));
  const [dir, setDir] = useState(() => ({ ...content.location.directions_url }));
  const c = content.location.coordinates;
  const [lat, setLat] = useState(String(c.value?.lat ?? ""));
  const [lng, setLng] = useState(String(c.value?.lng ?? ""));
  const [coordVerified, setCoordVerified] = useState(!!c.verified);
  const [showErrors, setShowErrors] = useState(false);
  const { save, saving } = useSaver();

  const errors = Object.fromEntries(FIELDS.map(([k, , , r]) => [k, checkField(contact[k], r)]));
  errors.directions = checkField(dir, rules.url);
  errors.coords = coordError(lat, lng);

  const onSave = () => {
    setShowErrors(true);
    if (Object.values(errors).some(Boolean)) return toast.error("Lütfen hatalı alanları düzeltin.");
    const coordsValue = lat.trim() && lng.trim() ? { lat: num(lat), lng: num(lng) } : null;
    save([
      ["contact", Object.fromEntries(FIELDS.map(([k]) => [k, clean(contact[k])]))],
      ["location", { coordinates: { value: coordsValue, verified: coordVerified }, directions_url: clean(dir) }],
    ]);
  };

  return (
    <section data-testid="admin-section-iletisim">
      <SectionHeader title="İletişim" text="Telefon ve WhatsApp için Türkiye numarası (+90) girin. Yalnızca dolu ve “Doğrulandı” işaretli alanlar canlı sitede görünür." />
      <div className="grid gap-4 md:grid-cols-2">
        {FIELDS.map(([k, label, ph]) => (
          <FieldEditor key={k} id={`contact-${k}`} label={label} placeholder={ph} field={contact[k]} error={showErrors && errors[k]} onChange={(f) => setContact({ ...contact, [k]: f })} />
        ))}
        <FieldEditor id="location-directions" label="Yol tarifi bağlantısı" placeholder="https://maps.app.goo.gl/..." field={dir} error={showErrors && errors.directions} onChange={setDir} />
        <div className="rounded-2xl border border-line bg-white p-5 space-y-3 md:col-span-2" data-testid="field-location-coordinates">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-wood">Harita koordinatları</p>
            <LiveIndicator live={!!(lat.trim() && lng.trim() && coordVerified && !errors.coords)} testId="field-location-coordinates-live" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-ink-muted space-y-1">Enlem
              <input inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} className="w-full rounded-xl border border-line px-4 py-3 text-base text-ink" data-testid="field-location-lat-input" />
            </label>
            <label className="text-sm text-ink-muted space-y-1">Boylam
              <input inputMode="decimal" value={lng} onChange={(e) => setLng(e.target.value)} className="w-full rounded-xl border border-line px-4 py-3 text-base text-ink" data-testid="field-location-lng-input" />
            </label>
          </div>
          {showErrors && errors.coords && <p className="text-sm text-[#B42318]" data-testid="field-location-coordinates-error">{errors.coords}</p>}
          <Checkbox id="v-coords" label="Doğrulandı" checked={coordVerified} onChange={setCoordVerified} testId="field-location-coordinates-verified" />
        </div>
      </div>
      <div className="mt-6"><SaveButton onClick={onSave} saving={saving} testId="admin-save-iletisim" /></div>
    </section>
  );
}
